<template>
  <div>
    <page-with-sidebar @sidebar-toggle="calcSidebarHeight"
                       :has-notification="contractStatus === contractStatuses.WARNING">
      <div slot="sidebar">
        <services-sidebar v-if="menuItems.length"
                          ref="servicesSidebar"
                          :loading="sidebarLoading"
                          :menu-items="menuItems"
                          :subpage-active="subpageOpen"
                          :selection-blocked="sidebarSelectionBlocked"
                          :has-notification="contractStatus === contractStatuses.WARNING"
                          @selected="handleMenuItemSelect"
                          @clear-subpage="handleMenuItemSelect"/>
      </div>
      <page-layout :has-notification="contractStatus === contractStatuses.WARNING">
        <div slot="pre-header">
          <account-warning-notification v-if="contractStatus === contractStatuses.WARNING"
                                        :visible="contractStatus === contractStatuses.WARNING"/>
        </div>
        <section slot="header">
          <transition name="fade" mode="out-in">
            <component v-if="Object.keys(selectedServiceInfo).length && !dataLoading" :is="headerComponent"
                       :key="'header'+headerKey" :header-info="selectedServiceHeaderInfo"
                       :balance="selectedServiceHeaderInfoBalance"></component>
            <div v-else class="header-placeholder"></div>
          </transition>
          <transition name="expand-tab">
            <scrolling-tab-header v-if="mainPageTabs.length && !dataLoading"
                                  :tab-list="mainPageTabs"
                                  @tab-change="handleMainPageTabChange"
                                  class="main-page-tabs"/>
          </transition>
        </section>

        <transition name="fade" mode="out-in">
          <section class="services-content" :key="componentKey">
            <div class="main-content">
              <loader-with-message v-if="dataLoading"
                                   :has-error="dataFetchError"
                                   @retry="handleMenuItemSelect(cachedMenuSelection)"/>
              <div v-else>
                <contract-obligation-bar full-width
                                         :contract-info="selectedServiceInfo"
                                         v-if="selectedServiceInfo.subscriptionState && getDurationUntilExpiry(selectedServiceInfo.contractEndDate) < 3 && isContractObligationVisible"/>
                <suspension-protection v-if="Object.keys(suspensionProtection).length && !selectedServiceSubType"
                                       :subscriber-identity="selectedServiceInfo.subscriberIdentity"
                                       :amount="suspensionProtection.formattedValue"
                                       @refresh-data="refreshData"/>
                <component :is="mainComponent"
                           :resources-data="selectedServiceInfo"
                           :unsuspend="unsuspendedUser"
                           :top-up-promo-message="topUpPromoMessage"
                           ref="mainComponent"
                           @selection-unblocked="unblockSidebar"
                           @refresh-data="refreshData"
                           @hide-contract-bar="hideContractBar"
                           @hide-banner="hideBanner"
                           @subpage-open="handleSubpageOpen"
                           @add-tabs="handleAddingTabs"/>
              </div>
            </div>
            <transition name="fade" mode="out-in">
              <div v-show="isBannerVisible && !dataFetchError && !dataLoading">
                <div v-if="installmentList.length" class="monthly-installment">
                  <h3 class="title">{{ $t('monthlyInstallment.monthlyInstallmentCardTitle') }}</h3>
                  <monthly-installment-card v-for="(installment, index) in installmentList"
                                            :key="'installment' + index"
                                            :installment-data="installment"/>
                </div>
                <div v-if="Object.keys(promoBanner).length" class="banner-space">
                  <promo-banner v-if="selectedServiceInfo.hasPersonalizedOffers"
                                :imageSM="promoBanner.background320"
                                :imageMD="promoBanner.background768"
                                :image="promoBanner.background1366"
                                :title="promoBanner.title"
                                :description="promoBanner.description"
                                :personalizedOffer="selectedServiceInfo.hasPersonalizedOffers"
                                :promo-type="selectedServiceType"
                                @go-to-offer="goToOffer"/>
                  <promo-banner v-else
                                :imageSM="promoBanner.background320"
                                :imageMD="promoBanner.background768"
                                :image="promoBanner.background1366"
                                :title="promoBanner.title"
                                :description="promoBanner.description"
                                :linkName="promoBanner.linkName"
                                :linkUrl="promoBanner.linkUrl"
                                overlay="gradient"
                                :personalizedOffer="selectedServiceInfo.hasPersonalizedOffers"
                                :promo-type="selectedServiceType"/>
                </div>
              </div>
            </transition>
          </section>
        </transition>
        <section v-if="shouldDisplayAddons(selectedServiceInfo) && !dataFetchError && !dataLoading">
          <addon-table :service-info="selectedServiceInfo"/>
        </section>
        <div class="main-content" v-if="!dataFetchError">
          <transition name="delayed-fade" mode="out-in">
            <contract-obligation-bar :contract-info="selectedServiceInfo"
                                     v-if="selectedServiceInfo.subscriptionState && getDurationUntilExpiry(selectedServiceInfo.contractEndDate) >= 3 && isContractObligationVisible"
                                     :key="'duration2'+componentKey"/>
          </transition>
        </div>
      </page-layout>
    </page-with-sidebar>
    <!-- keep it on bottom -->
    <connected-services-drawer v-if="isFirstLogin" @refresh-side-nav="refreshSidebarMenuItems"/>
  </div>
</template>

<script>
import ConnectedServicesDrawer from '@/components/services/connectServices/connected-services-drawer'
import PageWithSidebar from '@/components/common/main-layout/page-with-sidebar'
import PageLayout from '@/components/common/main-layout/page-layout'
import ServicesSidebar from '@/components/common/navigation/services-sidebar'
// service components
import HeaderServicesOverview from '@/components/services/headers/header-services-overview'
import MobileResources from '@/components/services/resources/mobile-resources'
import FixedResources from '@/components/services/resources/fixed-resources'
import PostpaidTopUp from '@/components/services/resources/postpaid-top-up'
import RoamingService from '@/components/services/resources/roaming-service'
import MobileListing from '@/components/services/resources/mobile-listing'
import InternetResources from '@/components/services/resources/internet-resources'
import TelevisionResources from '@/components/services/resources/television-resources'
import AccountStatusBlocked from '@/components/services/account-status/account-status-blocked'
import AccountStatusOnHold from '@/components/services/account-status/account-status-on-hold'
import MobileAddons from '@/components/addons/mobile-addons'
import TvAddons from '@/components/addons/tv-addons'
import TariffChange from '@/components/services/resources/tariff-change'
import LimitChange from '@/components/services/resources/limit-change'

// other components
import ContractObligationBar from '@/components/services/other/contract-obligation-bar'
import AccountWarningNotification from '@/components/services/account-status/account-warning-notification'
import MonthlyInstallmentCard from '@/components/services/other/monthly-installment-card'
import AddonTable from '@/components/services/other/addon-table'
import ScrollingTabHeader from '@/components/common/tabs/scrolling-tab-header'
import LoaderWithMessage from '@/components/common/loader-with-message'
import PromoBanner from '@/components/banners/promo-banner'
import SuspensionProtection from '@/components/services/other/suspension-protection'

// utils
import sidebarMenu from '@/mixins/sidebarMenu'
import { lsGet, lsSave } from '@/utils/localStorageController'
import { differenceInMonths, parseISO } from 'date-fns'
import { customRouter } from '@/customRouter'
import promoBannerApi from '@/components/banners/mixins/promo-banner-api'
import { addDataLayerEvent } from '@/services/dataLayer'
import { addUserIdDataLayerEvent } from '@/utils/addUserIdDataLayerEvent'

const contractObligationVisibleForSubtypes = ['']
const bannerVisibleForSubtypes = ['', 'topUp', 'limitChange', 'roaming'] // banner is visible for all items in top nav, but not for all in submenu. Add additional types if it should be visible
const contractStatuses = {
  WARNING: 'WARNING',
  ACTIVE: 'ACTIVE',
  BLOCKED: 'BLOCKED',
  ON_HOLD: 'ON_HOLD'
}
export default {
  name: 'page-services-overview',
  components: {
    TariffChange,
    LoaderWithMessage,
    TvAddons,
    ScrollingTabHeader,
    AddonTable,
    AccountStatusOnHold,
    AccountStatusBlocked,
    AccountWarningNotification,
    MonthlyInstallmentCard,
    ContractObligationBar,
    MobileResources,
    FixedResources,
    PostpaidTopUp,
    HeaderServicesOverview,
    ServicesSidebar,
    PageLayout,
    PageWithSidebar,
    ConnectedServicesDrawer,
    MobileListing,
    InternetResources,
    TelevisionResources,
    RoamingService,
    MobileAddons,
    PromoBanner,
    SuspensionProtection,
    LimitChange
  },
  data () {
    return {
      selectedServiceType: '',
      selectedServiceSubType: '',
      headerComponent: null,
      mainComponent: null,
      selectedServiceInfo: {},
      selectedServiceHeaderInfo: {},
      selectedServiceHeaderInfoBalance: {},
      componentKey: '',
      headerKey: '',
      contractBarVisible: true,
      bannerVisible: true,
      subpageOpen: false,
      hasPaymentNotification: false,
      contractStatus: '',
      installmentList: [],
      mainPageTabs: [],
      sidebarSelectionBlocked: false,
      sidebarBlockTempTimeout: null,
      dataLoading: false,
      dataFetchError: false,
      cachedMenuSelection: {},
      hasErrorTest: false,
      promoBanner: {},
      suspensionProtection: {},
      unsuspendedUser: {},
      topUpPromoMessage: ''
    }
  },
  computed: {
    isFirstLogin () {
      return lsGet('sc_firstLogin')
    },
    isBannerVisible () {
      return bannerVisibleForSubtypes.includes(this.selectedServiceSubType) && this.bannerVisible && this.contractStatus !== this.contractStatuses.BLOCKED && this.contractStatus !== this.contractStatuses.ON_HOLD
    },
    isContractObligationVisible () {
      return contractObligationVisibleForSubtypes.includes(this.selectedServiceSubType) && this.contractBarVisible
    },
    contractStatuses () {
      return Object.freeze(contractStatuses)
    }
  },
  mixins: [sidebarMenu, promoBannerApi],
  methods: {
    getUserInfo (id) {
      this.selectedServiceInfo = {}
      return new Promise((resolve, reject) => {
        this.$apiRequest.get(`user/service/details?subscriberIdentity=${id}`)
          .then(response => {
            this.selectedServiceInfo = response.data
            this.contractStatus = response.data.contractStatus
            this.installmentList = this.selectedServiceInfo.deviceBills || []
            this.suspensionProtection = response.data.suspensionProtection || {}
            this.unsuspendedUser = response.data.unsuspendedPrice || {}
            resolve(response.data.contractStatus)
          })
          .catch(e => {
            // this.handleErrorMessage(e.response.data)
            reject(new Error(''))
          })
      })
    },
    getHeaderInfo (id) {
      this.selectedServiceHeaderInfo = {}
      return new Promise((resolve, reject) => {
        this.$apiRequest.get(`user/service/header?subscriberIdentity=${id}`)
          .then(response => {
            this.selectedServiceHeaderInfo = response.data
            this.selectedServiceHeaderInfoBalance = response.data.balance
            this.topUpPromoMessage = response.data.topUpPromoMessage
            resolve()
          })
          .catch(e => {
            // this.handleErrorMessage(e.response.data)
            reject(new Error(''))
          })
      })
    },
    getUserBalance (id) {
      const data = {
        subscriberIdentity: id
      }
      this.$apiRequest.post('user/services/balance', data)
        .then(response => {
          this.selectedServiceHeaderInfoBalance = response.data
        })
    },
    checkContractStatus (data) {
      if (data.contractStatus) {
        this.contractStatus = contractStatuses[data.contractStatus]
      }
    },
    refreshData (id) {
      this.getUserInfo(id)
      this.getHeaderInfo(id)
      this.getUserBalance(id)
    },
    handleMenuItemSelect (payload) {
      const { id, type, subType } = payload

      this.cachedMenuSelection = payload

      // block selection in sidebar
      this.initSidebarBlock()

      // small trick to rerender components each time the service is changed
      // this way even for the services of the same type we will rerender the component
      // needed to run the animations again
      this.componentKey = new Date().getTime()

      this.selectedServiceType = type // maybe we won't need this
      this.selectedServiceSubType = subType
      this.contractBarVisible = true
      this.bannerVisible = true

      this.dataLoading = false
      this.dataFetchError = false
      if (!subType) {
        const callStack = [this.getUserInfo(id), this.getHeaderInfo(id)]
        this.dataLoading = true
        Promise.all(callStack)
          .then((res) => {
            const contractStatus = res[0]
            this.headerComponent = this.getHeaderComponent(type)
            this.mainComponent = this.getMainComponent(type, subType, contractStatus)
            this.fetchBanner(type)

            this.dataLoading = false
          })
          .catch(() => {
            this.dataFetchError = true
          })
          .finally(() => {
            if (this.$refs.servicesSidebar) {
              this.$refs.servicesSidebar.$emit('navigation-complete')
            }
            this.sidebarSelectionBlocked = false
          })
      } else {
        this.headerComponent = this.getHeaderComponent(type)
        this.mainComponent = this.getMainComponent(type, subType, this.contractStatus)
      }

      this.headerKey = new Date().getTime()

      this.handleSubpageOpen(false)
      this.getUserBalance(id)
    },
    getHeaderComponent (type) {
      switch (type) {
        case 'MOBILE_PREPAID':
          return 'header-services-overview'
        case 'MOBILE_POSTPAID':
          return 'header-services-overview'
        case 'FIXED_TELEPHONY':
          return 'header-services-overview'
        default:
          return 'header-services-overview'
      }
    },
    getMainComponent (type, subType, status) {
      // reset tabs if any
      this.mainPageTabs = []

      if (type === 'MOBILE_PREPAID') {
        if (subType === 'listing') {
          return 'mobile-listing'
        } else if (subType === 'topUp') {
          return this.getAuthorizedComponentByStatus(status, 'postpaid-top-up')
        } else if (subType === 'roaming') {
          return this.getAuthorizedComponentByStatus(status, 'roaming-service')
        } else if (subType === 'addons') {
          return this.getAuthorizedComponentByStatus(status, 'mobile-addons')
        } else {
          return this.getAuthorizedComponentByStatus(status, 'mobile-resources')
        }
      } else if (type === 'MOBILE_POSTPAID') {
        if (subType === 'listing') {
          return this.getAuthorizedComponentByStatus(status, 'mobile-listing')
        } else if (subType === 'topUp') {
          return this.getAuthorizedComponentByStatus(status, 'postpaid-top-up')
        } else if (subType === 'roaming') {
          return this.getAuthorizedComponentByStatus(status, 'roaming-service')
        } else if (subType === 'addons') {
          return this.getAuthorizedComponentByStatus(status, 'mobile-addons')
        } else if (subType === 'tariffChange') {
          return this.getAuthorizedComponentByStatus(status, 'tariff-change')
        } else if (subType === 'limitChange') {
          return this.getAuthorizedComponentByStatus(status, 'limit-change')
        } else {
          return this.getAuthorizedComponentByStatus(status, 'mobile-resources')
        }
      } else if (type === 'FIXED_TELEPHONY') {
        return this.getAuthorizedComponentByStatus(status, 'fixed-resources')
      } else if (type === 'INTERNET') {
        return this.getAuthorizedComponentByStatus(status, 'internet-resources')
      } else if (type === 'TELEVISION') {
        if (subType === 'addons') {
          return this.getAuthorizedComponentByStatus(status, 'tv-addons')
        } else {
          return this.getAuthorizedComponentByStatus(status, 'television-resources')
        }
      }
    },
    getAuthorizedComponentByStatus (status, defaultComponent) {
      if (status === this.contractStatuses.BLOCKED) {
        return 'account-status-blocked'
      } else if (status === this.contractStatuses.ON_HOLD) {
        return 'account-status-on-hold'
      } else {
        return defaultComponent
      }
    },
    getDurationUntilExpiry (date) {
      if (!date) return ''
      return differenceInMonths(parseISO(date), new Date())
    },
    calcSidebarHeight () {
      this.$refs.servicesSidebar.setSidebarHeight()
    },
    refreshSidebarMenuItems () {
      this.getSidebarMenuItems('services', true)
    },
    hideContractBar (state) {
      this.contractBarVisible = !state
    },
    hideBanner (state) {
      this.bannerVisible = !state
    },
    handleSubpageOpen (state) {
      this.subpageOpen = state
    },
    shouldDisplayAddons (data = {}) {
      const allowedTypes = ['TELEVISION', 'MOBILE_POSTPAID', 'MOBILE_PREPAID']
      return allowedTypes.includes(data.subscriptionType) && data.activatedAddons && data.activatedAddons.length && !this.selectedServiceSubType
    },
    handleAddingTabs (data = []) {
      this.mainPageTabs = data
    },
    handleMainPageTabChange (tabName) {
      // emit event directly on component
      this.$refs.mainComponent.$emit('tab-change', tabName)
    },
    unblockSidebar () {
      setTimeout(() => {
        this.sidebarSelectionBlocked = false
      }, 300)

      // clear timeout if exists
      clearTimeout(this.sidebarBlockTempTimeout)
      this.sidebarBlockTempTimeout = null
    },
    initSidebarBlock () {
      this.sidebarSelectionBlocked = true

      // after 10s, unblock sidebar anyway
      this.sidebarBlockTempTimeout = setTimeout(() => {
        this.unblockSidebar()
      }, 10000)
    },
    goToOffer () {
      lsSave('sc_customer_id', this.selectedServiceInfo.customerId)
      customRouter.goTo({ name: 'personalizedOffer' })
    }
  },
  mounted () {
    this.getSidebarMenuItems('services') // sidebarMenu mixin
    addDataLayerEvent({
      event: 'screen',
      name: 'service_overview'
    })
    addUserIdDataLayerEvent()
  }
}
</script>

<style scoped lang="scss">
.services-content {
  display: flex;
  flex-direction: column;
  padding: 2rem 0;

  @media (map_get($breakpoints, lg)) {
    flex-direction: row;
  }

  .monthly-installment {
    flex: 1 1 100%;
    padding: 2rem;
    margin-left: 0;

    @media (map_get($breakpoints, lg)) {
      padding-left: 2rem;
      padding-right: 0;
      margin-left: 2rem;
    }

    .title {
      margin-bottom: 1.6rem;
    }
  }

  .banner-space {
    padding: 2rem;

    @media (map_get($breakpoints, lg)) {
      padding-left: 2rem;
      padding-right: 0;
      margin-left: 2rem;
      width: 34rem;
    }
  }

  .banner-placeholder {
    flex: 1 1 100%;
    margin-left: 2rem;
    border: 1px dashed $dark-grey;
    border-radius: 5px;
    text-align: center;
    padding: 2rem;
    margin-bottom: auto;
    opacity: 0;
    min-width: 340px;

    @media (map_get($breakpoints, lg)) {
      flex: 0 0 340px;
      padding-left: 2rem;
    }
  }
}

.main-content {
  flex: 1 1 auto;
  padding: 0 2rem;
  max-width: 100%;
  width: 100%;

  @media (map_get($breakpoints, lg)) {
    padding-right: 0;
  }
}

.header-placeholder {
  min-height: 71px;
}

.main-page-tabs {
  margin-bottom: -1.6rem;
  height: 51px;

  @media (map_get($breakpoints, sm)) {
    height: 54px;
  }
}
</style>
